import { ButtonWrapper } from "./_Button";
import classNames from "classnames";
import { useEditorPlugin, useEditorSelector } from "platejs/react";
import type { TElement } from "platejs";
import { FontSizePlugin } from "@platejs/basic-styles/react";
import { toUnitLess } from "@platejs/basic-styles";
import { KEYS } from "platejs";
import { useState } from "react";
import { Popover } from "radix-ui";
import styles from "../../styles/SlateEditor.module.scss";

const DEFAULT_FONT_SIZE = "16";

const FONT_SIZE_MAP = {
  [KEYS.h1]: "36",
  [KEYS.h2]: "24",
  [KEYS.h3]: "20",
} as const;

const FONT_SIZES = [
  "8",
  "9",
  "10",
  "12",
  "14",
  "16",
  "18",
  "24",
  "30",
  "36",
  "48",
  "60",
  "72",
  "96",
] as const;

export function FontSizeWidget() {
  const decreaseSizeButtonClassNames = classNames("fa", `fa-minus`);
  const increaseSizeButtonClassNames = classNames("fa", `fa-plus`);

  const [inputValue, setInputValue] = useState(DEFAULT_FONT_SIZE);
  const [isFocused, setIsFocused] = useState(false);

  const { editor, tf } = useEditorPlugin(FontSizePlugin);

  const cursorFontSize = useEditorSelector((editor) => {
    const fontSize = editor.api.marks()?.[FontSizePlugin.key];

    if (fontSize) {
      return toUnitLess(String(fontSize));
    }

    const [block] = editor.api.block<TElement>() || [];

    if (!block?.type) return DEFAULT_FONT_SIZE;

    return block.type in FONT_SIZE_MAP
      ? FONT_SIZE_MAP[block.type as keyof typeof FONT_SIZE_MAP]
      : DEFAULT_FONT_SIZE;
  }, []);

  const displayValue = isFocused ? inputValue : cursorFontSize;

  const handleFontSizeChange = (delta: number) => {
    const newSize = Number(displayValue) + delta;
    tf.fontSize.addMark(`${newSize}px`);
  };

  const handleInputChange = () => {
    const newSize = toUnitLess(inputValue);

    if (Number.parseInt(newSize) < 1 || Number.parseInt(newSize) > 100) {
      editor.tf.focus();

      return;
    }
    if (newSize !== toUnitLess(cursorFontSize)) {
      tf.fontSize.addMark(`${newSize}px`);
    }

    editor.tf.focus();
  };

  return (
    <div className={styles.fontSizeWidgetWrapper}>
      <ButtonWrapper
        style={{ borderBottomRightRadius: 0, borderTopRightRadius: 0 }}
        className={decreaseSizeButtonClassNames}
        onClick={() => handleFontSizeChange(-1)}
      />
      <Popover.Root open={isFocused}>
        <Popover.Trigger>
          <input
            className={styles.fontSizeInput}
            value={displayValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => {
              setIsFocused(true);
              setInputValue(toUnitLess(cursorFontSize));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleInputChange();
              }
            }}
            type={"text"}
          />
        </Popover.Trigger>
        <Popover.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={() => {
            setIsFocused(false);
            handleInputChange();
          }}
          className={styles.fontSizePopoverContent}
        >
          {FONT_SIZES.map((size) => (
            <div key={size}>
              <button
                onClick={() => {
                  tf.fontSize.addMark(`${size}px`);
                  setIsFocused(false);
                }}
              >
                {size}
              </button>
            </div>
          ))}
        </Popover.Content>
      </Popover.Root>
      <ButtonWrapper
        style={{ borderBottomLeftRadius: 0, borderTopLeftRadius: 0 }}
        className={increaseSizeButtonClassNames}
        onClick={() => handleFontSizeChange(1)}
      />
    </div>
  );
}
